import React, { useCallback, useMemo, useState } from 'react';

import { Car, DollarSign, Inbox, LayoutDashboard, Package, Settings, Tag, Users } from 'lucide-react';

const SidebarContext = React.createContext(null);

// Nav per the .pen Sidebar component; unbuilt pages render as inert
// coming-soon rows and flip to links as their milestones land.
const top = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    to: '/app/dashboard',
    icon: <LayoutDashboard />,
  },
  {
    key: 'inventory',
    label: 'Inventory',
    to: '/app/inventory',
    icon: <Package />,
  },
  {
    key: 'garage',
    label: 'Garage',
    to: '/app/garage',
    icon: <Car />,
    // Keep Garage lit on /app/garage/:id (the ROI page).
    exact: false,
  },
  {
    key: 'listings',
    label: 'Listings',
    to: '/app/listings',
    icon: <Tag />,
  },
  {
    key: 'sales',
    label: 'Sales',
    to: '/app/sales',
    icon: <DollarSign />,
  },
  {
    key: 'customers',
    label: 'Customers',
    to: '/app/customers',
    icon: <Users />,
    // Keep Customers lit on /app/customers/:id (the detail page).
    exact: false,
  },
  {
    key: 'leads',
    label: 'Leads',
    to: '/app/leads',
    icon: <Inbox />,
  },
];

const bottom = [
  {
    key: 'settings',
    label: 'Settings',
    to: '/app/settings',
    icon: <Settings />,
    // Keep Settings lit across its child tabs (/app/settings/profile, etc.).
    exact: false,
  },
];


function SidebarProvider({ children }) {
  // Mobile starts closed (the drawer overlays the page); desktop starts open.
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );

  const open = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  const toggle = useCallback(() => {
    setIsCollapsed((prevValue) => !prevValue);
  }, []);

  const contextValue = useMemo(() => ({
    isCollapsed,
    open,
    collapse,
    toggle,
    items: {
      top,
      bottom,
    },
  }), [collapse, isCollapsed, open, toggle]);

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>;
}

SidebarProvider.displayName = 'SidebarProvider';
export { SidebarContext, SidebarProvider };
